import React, { useState, useEffect, useRef, useId } from "react";
import { useTranslation } from 'react-i18next';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import MultiSelect from './MultiSelect';
import SearchCombobox from 'reusable/RemoteSearch/SearchCombobox';
import BirthdayInput from 'reusable/BirthdayInput';
import { FetchRemotePage } from 'reusable/RemoteSearch/remotePage';

/**
 * EV-493。複数選択の候補をサーバーで検索する（全件を options で渡さない）。あれば options の代わりに使う。
 * resetKey が変わったら候補を読み直す（保存の失敗の後など）。
 */
export interface FieldSearch {
    fetchPage: FetchRemotePage<{ name: string; id: string | number }>;
    resetKey?: string | number;
}

export interface Field {
    type: string;
    label: string;
    name: string;
    value: string | string[];
    options?: { name: string; id: string }[];
    /** EV-493。候補をサーバーで検索する（複数選択の欄）。 */
    search?: FieldSearch;
    required?: boolean;
    autoGenerate?: boolean;
    readOnly?: boolean;
    /** 項目の下に出す説明（その項目を使えない理由など）。 */
    note?: string;
    /** 説明の横に出す操作（読み直しなど）。 */
    noteAction?: { label: string; onClick: () => void };
    /** I12-2。入力欄の例（文字の欄）。 */
    placeholder?: string;
}

interface EditPopupProps {
    title: string;
    fields: Field[];
    save: string;
    initialValues: { [key: string]: any };
    /** false を返す（または例外）と、保存できなかったとしてダイアログを閉じない。 */
    onSave: (updatedValues: { [key: string]: any }) => void | boolean | Promise<void | boolean>;
    onClose: () => void;
}

const EditPopup: React.FC<EditPopupProps> = ({ title, fields, save, initialValues, onSave, onClose }) => {
    const { t, i18n } = useTranslation('driver');
    const [editFormValue, setEditFormValue] = useState<{ [key: string]: any }>({});
    const [showOptions, setShowOptions] = useState<{ [key: string]: boolean }>({});
    // ADM-16。ダイアログの名前（見出し）・Escape・フォーカスの閉じ込めと戻し。
    const titleId = useId();
    // ADM-16。項目名（label）と入力欄を結び付ける（読み上げで何の欄か分かるように）。
    const fieldId = useId();
    const dialogRef = useModalDialog(true, onClose);

    // ADM-15。開いたとき（または編集対象が変わったとき）だけ初期値にする。
    // 親の再描画のたびに作り直される fields で初期化し直すと、保存に失敗して
    // エラーを出したときに入力が消えていた。
    // EV-032。複数選択の項目は、読み取り専用に切り替わったときも初期値（field.value）に戻す。
    // 選べなくなった項目に、利用者が選んだまま保存されていない値を出し続けない。
    const signatureOf = (field: Field) =>
        JSON.stringify(
            field.type === "multi-select" ? [field.value, Boolean(field.readOnly)] : field.value
        );
    const fieldsKey = JSON.stringify(fields.map((field) => [field.label, signatureOf(field)]));
    // PR #99 レビュー。編集対象（initialValues）が変わったときは全体を初期値にし、
    // 同じ編集対象で一部の項目の初期値だけが変わったとき（保存の競合の後に割当を
    // 読み直したときなど）は、その項目だけを初期値に戻す。全体を作り直すと、
    // ほかの項目の未保存の入力が黙って消えていた。
    // EV-033。項目は name（無ければ label）で対応付ける。言語の切り替えでラベルが変わっても、
    // 同じ項目の入力を新しいラベルへ移す。
    const previous = useRef<{
        initialValues: unknown;
        values: Record<string, string>;
        labels: Record<string, string>;
    } | null>(null);
    useEffect(() => {
        const initialOf = (field: Field) =>
            field.type === "multi-select" && Array.isArray(field.value)
                ? field.value.map(val => JSON.parse(val))
                : undefined;
        const keyOf = (field: Field) => field.name || field.label;
        const values = Object.fromEntries(
            fields.map((field) => [keyOf(field), signatureOf(field)])
        );
        const labels = Object.fromEntries(fields.map((field) => [keyOf(field), field.label]));
        const prev = previous.current;
        previous.current = { initialValues, values, labels };
        if (!prev || prev.initialValues !== initialValues) {
            const parsedInitialValues = { ...initialValues };
            fields.forEach(field => {
                const initial = initialOf(field);
                if (initial !== undefined) parsedInitialValues[field.label] = initial;
            });
            setEditFormValue(parsedInitialValues);
            return;
        }
        setEditFormValue(state => {
            const next = { ...state };
            fields.forEach(field => {
                const key = keyOf(field);
                const oldLabel = prev.labels[key];
                if (oldLabel !== undefined && oldLabel !== field.label && oldLabel in next) {
                    next[field.label] = next[oldLabel];
                    delete next[oldLabel];
                }
                if (prev.values[key] === values[key]) return;
                // 複数選択（割当など）は、初期値が変わったら新しい初期値に戻す（割当の読み直し）。
                // EV-033。それ以外の項目は利用者の入力を保つ。以前は state から消しており、
                // 入力が消えて field.value の表示に戻り、保存の値からも落ちていた。
                // 入力していない項目は state に無いので、新しい初期値が表示される。
                const initial = initialOf(field);
                if (initial !== undefined) next[field.label] = initial;
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues, fieldsKey]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEditFormValue({
            ...editFormValue,
            [name]: value,
        });
    };

    const handleMultiSelectChange = (e: React.ChangeEvent<HTMLInputElement>, label: string) => {
        const { value, checked } = e.target;
        const parsedValue = JSON.parse(value);
        setEditFormValue(prevState => {
            const selectedOptions = prevState[label] || [];
            if (checked) {
                return { ...prevState, [label]: [...selectedOptions, parsedValue] };
            } else {
                return { ...prevState, [label]: selectedOptions.filter((option: any) => option.id !== parsedValue.id) };
            }
        });
    };

    const setOptionsOpen = (label: string, open: boolean) => {
        setShowOptions(prevState => ({
            ...prevState,
            [label]: open
        }));
    };

    // EV-493。検索の欄で候補を選んだ（選んでいれば何もしない）。
    const addOption = (label: string, option: any) => {
        setEditFormValue(prevState => {
            const selectedOptions = prevState[label] || [];
            if (selectedOptions.some((item: any) => item.id === option.id)) return prevState;
            return { ...prevState, [label]: [...selectedOptions, { name: option.name, id: option.id }] };
        });
    };

    const removeOption = (label: string, option: any) => {
        setEditFormValue(prevState => ({
            ...prevState,
            [label]: prevState[label].filter((item: any) => item.id !== option.id)
        }));
    };

    // ADM-15。保存が終わるまで待ち、失敗したら閉じない（入力を残す）。
    // 以前は完了を待たずに閉じており、保存に失敗すると入力が消えていた。
    const handleSaveChanges = async () => {
        try {
            const result = await onSave(editFormValue);
            if (result === false) return;
        } catch (error) {
            console.error('Failed to save', error);
            return;
        }
        onClose();
    };

    return inModalLayer(
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-50">
            <div
                className="bg-white rounded-md shadow-md w-full max-w-2xl relative"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                ref={dialogRef}
                tabIndex={-1}
            >
                <button
                    type="button"
                    className="absolute top-2 right-4 text-gray-600 hover:text-gray-800 text-title"
                    onClick={onClose}
                    aria-label={t('ui.close', { ns: 'common' })}
                >
                    &times;
                </button>
                <h2 id={titleId} className="text-subtitle-1 font-semi-bold px-6 py-4">{title}</h2>
                <div className="border-y">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-y-0 px-6">
                        {fields.map((field, index) => (
                            <div key={index} className="my-4">
                                <label className="block mb-2" htmlFor={`${fieldId}-${index}`}>
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                {field.type === "text" || field.type === "email" || field.type === "number" ? (
                                    <input
                                        type={field.type}
                                        name={field.label}
                                        id={`${fieldId}-${index}`}
                                        className={`border p-2 rounded-md w-full ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''}`}
                                        value={editFormValue[field.label] ?? field.value ?? ""}
                                        onChange={handleInputChange}
                                        readOnly={field.readOnly}
                                        placeholder={field.placeholder}
                                    />
                                ) : field.type === "birthday" ? (
                                    // I12-1。生年月日は年・月・日の3つの選択（そろったときだけ「YYYY-MM-DD」）。
                                    <BirthdayInput
                                        id={`${fieldId}-${index}`}
                                        label={field.label}
                                        value={(editFormValue[field.label] ?? field.value ?? "").toString().split('T')[0]}
                                        onChange={(value) => setEditFormValue((prev) => ({ ...prev, [field.label]: value }))}
                                        disabled={field.readOnly}
                                    />
                                ) : field.type === "calendar" ? (
                                    <input
                                        type="date"
                                        name={field.label}
                                        id={`${fieldId}-${index}`}
                                        className={`border p-2 rounded-md w-full ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''}`}
                                        value={editFormValue[field.label]?.split('T')[0] ?? field.value ?? ""}
                                        onChange={handleInputChange}
                                        readOnly={field.readOnly}
                                    />
                                ) : field.type === "dropdown" ? (
                                    <select
                                        name={field.label}
                                        id={`${fieldId}-${index}`}
                                        className={`border p-2 rounded-md w-full ${field.readOnly ? 'bg-gray-200 text-gray-600' : ''}`}
                                        value={editFormValue[field.label] ?? field.value ?? ""}
                                        onChange={handleInputChange}
                                        disabled={field.readOnly}
                                    >
                                        {field.options?.map((option, idx) => (
                                            <option key={idx} value={option.name}>
                                                {/* EV-338。候補（機器）の名前が空なら「-」（EV-311。空の項目にしない）。 */}
                                                {option.name || '-'}
                                            </option>
                                        ))}
                                    </select>
                                ) : field.type === "multi-select" && field.search ? (
                                    <SearchCombobox<{ name: string; id: string | number }>
                                        id={`${fieldId}-${index}`}
                                        label={field.label}
                                        describedBy={field.note ? `${fieldId}-${index}-note` : undefined}
                                        multiple
                                        selected={Array.isArray(editFormValue[field.label]) ? editFormValue[field.label] : []}
                                        fetchPage={field.search.fetchPage}
                                        resetKey={field.search.resetKey}
                                        getKey={(option) => option.id}
                                        getLabel={(option) => option.name}
                                        onSelect={(option) => addOption(field.label, option)}
                                        onRemove={(option) => removeOption(field.label, option)}
                                        readOnly={field.readOnly}
                                        boxClassName={field.readOnly ? 'bg-gray-200 text-gray-600' : ''}
                                    />
                                ) : field.type === "multi-select" ? (
                                    <MultiSelect
                                        id={`${fieldId}-${index}`}
                                        label={field.label}
                                        describedBy={field.note ? `${fieldId}-${index}-note` : undefined}
                                        name={field.label}
                                        options={field.options}
                                        selected={Array.isArray(editFormValue[field.label]) ? editFormValue[field.label] : []}
                                        open={Boolean(showOptions[field.label])}
                                        onOpenChange={(open) => setOptionsOpen(field.label, open)}
                                        onChange={(e) => handleMultiSelectChange(e, field.label)}
                                        onRemove={(option) => removeOption(field.label, option)}
                                        readOnly={field.readOnly}
                                        boxClassName={field.readOnly ? 'bg-gray-200 text-gray-600' : ''}
                                    />
                                ) : null}
                                {/* PR #253 の Codex の P2（4257906741）。注記は知らせとして読み上げ、機器の欄に結ぶ（固定の id）。 */}
                                {field.note && (
                                    <p id={`${fieldId}-${index}-note`} role="alert" className="mt-1 text-red-500">{field.note}</p>
                                )}
                                {field.noteAction && (
                                    <button
                                        type="button"
                                        className="mt-1 rounded-md border px-3 py-1 text-primary/300"
                                        onClick={field.noteAction.onClick}
                                    >
                                        {field.noteAction.label}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex justify-between space-x-2 px-6 py-4 font-semi-bold">
                    <button type="button"
                        className="flex-1 py-2 px-4 bg-gray-300 text-primary/300 rounded-md"
                        onClick={onClose}
                    >
                        {t('edit.cancel')}
                    </button>
                    <button type="button"
                        className="flex-1 py-2 px-4 bg-primary/300 text-white rounded-md"
                        onClick={handleSaveChanges}
                    >
                        {save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditPopup;